import { apiClient } from './client';

/**
 * 교육 · 자격 · 어학 API (인사, migration 135 · 시안 A22).
 *
 * 서버: `apps/chat-server/src/modules/hr/trainings.routes.ts`.
 *
 * ★★ **만료 상태·이수율·지표는 전부 서버가 센다.** 화면은 그리기만 한다 — 「임박」 의 창(90일)이
 *    두 곳에 있으면 목록과 지표가 서로 다른 말을 한다.
 * ★ **옛 서버에는 이 라우트가 없다**(404). 부르는 쪽이 실패를 삼키고 화면을 접는다.
 */

export const COURSE_KINDS = ['STATUTORY', 'INTERNAL'] as const;
export type CourseKind = (typeof COURSE_KINDS)[number];
export const COURSE_KIND_LABEL: Record<string, string> = {
  STATUTORY: '법정교육', INTERNAL: '사내교육',
};

export const COURSE_CYCLES = ['ANNUAL', 'HALF', 'QUARTER', 'ONCE'] as const;
export type CourseCycle = (typeof COURSE_CYCLES)[number];
export const COURSE_CYCLE_LABEL: Record<string, string> = {
  ANNUAL: '연 1회', HALF: '반기', QUARTER: '분기', ONCE: '1회',
};

export const CREDENTIAL_KINDS = ['LICENSE', 'LANGUAGE'] as const;
export type CredentialKind = (typeof CREDENTIAL_KINDS)[number];
export const CREDENTIAL_KIND_LABEL: Record<string, string> = {
  LICENSE: '자격', LANGUAGE: '어학',
};

/** `NONE`(만료 없음) · `VALID` · `SOON`(창 안) · `EXPIRED`(지남). */
export type ExpiryState = 'NONE' | 'VALID' | 'SOON' | 'EXPIRED';
/** `DONE`(전원 이수) · `OVERDUE`(기한이 지났는데 남음) · `OPEN`(진행 중). */
export type CourseState = 'DONE' | 'OVERDUE' | 'OPEN';

/** `ALL`(재직자 전원 — **기본값이 곧 기존 동작**) · `SELECTED`(규칙으로 좁힌다). */
export const TARGET_SCOPES = ['ALL', 'SELECTED'] as const;
export type TargetScope = (typeof TARGET_SCOPES)[number];

export const TARGET_KINDS = ['TEAM', 'USER'] as const;
export type TargetKind = (typeof TARGET_KINDS)[number];

export interface CourseDto {
  id: string;
  kind: string;
  kindLabel: string;
  name: string;
  cycle: string;
  cycleLabel: string;
  dueOn: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  method: string | null;
  note: string | null;
  active: boolean;
  targetScope: string;
  /**
   * 대상은 아닌데 이수 기록이 있는 사람 수.
   * ★ 그 사람은 **실제로 들었다** — 기록은 남고, 모수·분자 어느 쪽에도 안 들어간다.
   */
  outsiders: number;
  done: number;
  /** 대상 인원 — `ALL` 이면 재직자 전원, `SELECTED` 면 규칙으로 좁힌 수다. */
  target: number;
  missing: number;
  rate: number;
  state: CourseState;
  /** 기한까지 남은 날 — 없으면 null, 지났으면 음수. */
  dueInDays: number | null;
}

export interface CredentialDto {
  id: string;
  userId: string;
  userName: string | null;
  employeeNumber: string | null;
  kind: string;
  kindLabel: string;
  name: string;
  issuer: string | null;
  score: string | null;
  /** 「TOEIC 905」 — 서버가 이름과 점수를 합쳐 준다(저장은 따로다). */
  title: string;
  acquiredOn: string;
  expiresOn: string | null;
  expiry: ExpiryState;
  /** 남은 날 — 만료 없으면 null, 지났으면 음수. */
  daysLeft: number | null;
  hasProof: boolean;
  /** 증명서에 실을 수 있나 — **유효하고 증빙이 있을 때만**. */
  onCertificate: boolean;
  note: string | null;
}

export interface TrainingSummaryDto {
  statutoryCourses: number;
  statutoryDone: number;
  /** 법정교육을 하나라도 안 들은 **사람 수**(건수가 아니다). */
  notCompleted: number;
  /** 미이수가 가장 많은 과정 — 없으면 null(이름을 지어내지 않는다). */
  worstCourse: string | null;
  holders: number;
  credentials: number;
  expiringSoon: number;
  expired: number;
}

export interface TrainingBoardDto {
  /** 이수 대상의 모수 — 재직자 전원. */
  headcount: number;
  summary: TrainingSummaryDto;
  courses: CourseDto[];
  /** 접힌 과정 — 지난 이수가 가리키는 이름이라 지우지 않는다. */
  archived: CourseDto[];
  credentials: CredentialDto[];
  /** 「만료가 다가오는 것」 — **지난 것이 맨 앞**, 그다음 임박한 순. */
  expiring: CredentialDto[];
  windowDays: number;
}

export async function fetchTrainings(): Promise<TrainingBoardDto> {
  const { data } = await apiClient.get<TrainingBoardDto>('/hr/trainings');
  return data;
}

export interface RosterRowDto {
  userId: string;
  name: string;
  employeeNumber: string | null;
  done: boolean;
  completedOn: string | null;
}

export interface CourseRosterDto {
  course: CourseDto;
  /** 대상자만. 좁힌 과정이면 그 사람들만 선다. */
  rows: RosterRowDto[];
  /** 대상은 아닌데 이수 기록이 있는 사람 — 지우지 않고 따로 보여 준다. */
  outsiders: RosterRowDto[];
}

export async function fetchCourseRoster(courseId: string): Promise<CourseRosterDto> {
  const { data } = await apiClient.get<CourseRosterDto>(`/hr/trainings/courses/${courseId}`);
  return data;
}

export interface CourseInput {
  name: string;
  targetScope?: TargetScope;
  kind?: CourseKind;
  cycle?: CourseCycle;
  dueOn?: string | null;
  periodStart?: string | null;
  periodEnd?: string | null;
  method?: string | null;
  note?: string | null;
  active?: boolean;
}

export async function createCourse(input: CourseInput): Promise<CourseDto> {
  const { data } = await apiClient.post<{ course: CourseDto }>('/hr/trainings/courses', input);
  return data.course;
}

export async function updateCourse(id: string, patch: Partial<CourseInput>): Promise<CourseDto> {
  const { data } = await apiClient.patch<{ course: CourseDto }>(`/hr/trainings/courses/${id}`, patch);
  return data.course;
}

export async function deleteCourse(id: string): Promise<void> {
  await apiClient.delete(`/hr/trainings/courses/${id}`);
}

/** 이수 표시 — **여러 번 눌러도 안전하다**(이수일이 밀리지 않는다). `done:false` 면 되돌린다. */
export async function markCompletion(
  courseId: string, userId: string, done = true,
): Promise<{ userId: string; done: boolean; completedOn: string | null }> {
  const { data } = await apiClient.post<{ userId: string; done: boolean; completedOn: string | null }>(
    `/hr/trainings/courses/${courseId}/completions`, { userId, done },
  );
  return data;
}

export interface CourseTargetDto {
  id: string;
  kind: TargetKind;
  teamId: string | null;
  userId: string | null;
  /** 화면에 그대로 적는 이름(팀 이름 또는 사람 이름). */
  label: string;
}

export async function fetchCourseTargets(courseId: string): Promise<{
  targetScope: string; rows: CourseTargetDto[];
}> {
  const { data } = await apiClient.get<{ targetScope: string; rows: CourseTargetDto[] }>(
    `/hr/trainings/courses/${courseId}/targets`,
  );
  return data;
}

/**
 * 대상 규칙 추가 — 팀 하나 **또는** 사람 하나(둘 다 보내면 400).
 * ★ 첫 규칙을 넣으면 서버가 범위도 `SELECTED` 로 함께 좁힌다.
 */
export async function addCourseTarget(
  courseId: string, input: { kind: TargetKind; teamId?: string; userId?: string },
): Promise<{ id: string; targetScope: string }> {
  const { data } = await apiClient.post<{ id: string; targetScope: string }>(
    `/hr/trainings/courses/${courseId}/targets`, input,
  );
  return data;
}

/** ★ 마지막 규칙을 지워도 범위는 안 돌아간다 — «0명» 은 «아직 안 골랐다» 는 뜻이다. */
export async function removeCourseTarget(id: string): Promise<void> {
  await apiClient.delete(`/hr/trainings/targets/${id}`);
}

export interface CredentialInput {
  userId: string;
  kind: CredentialKind;
  name: string;
  issuer?: string | null;
  score?: string | null;
  acquiredOn: string;
  /** 안 보내면 **만료 없음**이다. 먼 날짜로 채우지 말 것. */
  expiresOn?: string | null;
  hasProof?: boolean;
  note?: string | null;
}

export async function createCredential(input: CredentialInput): Promise<CredentialDto> {
  const { data } = await apiClient.post<{ credential: CredentialDto }>('/hr/trainings/credentials', input);
  return data.credential;
}

export async function updateCredential(
  id: string, patch: Partial<Omit<CredentialInput, 'userId' | 'kind'>>,
): Promise<CredentialDto> {
  const { data } = await apiClient.patch<{ credential: CredentialDto }>(`/hr/trainings/credentials/${id}`, patch);
  return data.credential;
}

export async function deleteCredential(id: string): Promise<void> {
  await apiClient.delete(`/hr/trainings/credentials/${id}`);
}

export interface UserTrainingDto {
  courses: Array<{
    id: string;
    name: string;
    kind: string;
    kindLabel: string;
    dueOn: string | null;
    done: boolean;
    completedOn: string | null;
  }>;
  credentials: CredentialDto[];
}

/** 한 사람 — 담당자와 **본인**이 같은 라우트를 쓴다(시안 B2 가 쓸 자리). */
export async function fetchUserTrainings(userId: string): Promise<UserTrainingDto> {
  const { data } = await apiClient.get<UserTrainingDto>(`/hr/trainings/user/${userId}`);
  return data;
}
